"use client";

import { useEffect, useRef, useState } from "react";
import type * as T from "three";

export type Three = typeof import("three");

export interface SceneHandle {
  scene: T.Scene;
  camera: T.Camera;
  /** Advances the animation. `dt` and `elapsed` are in seconds and only tick while the loop runs. */
  update?: (dt: number, elapsed: number) => void;
  /** Container size in CSS px; the renderer is already resized. */
  resize: (width: number, height: number) => void;
  /** Extra teardown (listeners, observers). Scene contents are disposed by the hook. */
  dispose?: () => void;
}

export interface SetupContext {
  THREE: Three;
  renderer: T.WebGLRenderer;
  canvas: HTMLCanvasElement;
  container: HTMLElement;
  reducedMotion: boolean;
  isCancelled: () => boolean;
}

export type SceneSetup = (ctx: SetupContext) => Promise<SceneHandle | null>;

interface Options {
  /** Multiplier on devicePixelRatio before the cap (small canvases can afford supersampling). */
  dprScale?: number;
  maxDpr?: number;
}

/** Disposes every geometry, material and texture reachable from `root`. */
export function disposeObject(root: T.Object3D) {
  root.traverse((obj) => {
    const mesh = obj as T.Mesh;
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    mats.forEach((mat) => {
      Object.values(mat).forEach((v) => {
        if (v && (v as T.Texture).isTexture) (v as T.Texture).dispose();
      });
      mat.dispose();
    });
  });
  const scene = root as T.Scene;
  if (scene.isScene) scene.environment?.dispose();
}

/**
 * Lazily mounts a three.js scene into the returned container:
 * three is imported and the renderer created only when the box first comes near the viewport,
 * the loop pauses off-screen and in hidden tabs, and reduced motion gets a single static frame.
 * `ready` flips once the first frame is on screen (keep the static fallback until then).
 */
export function useThreeCanvas(setup: SceneSetup, { dprScale = 1, maxDpr = 2 }: Options = {}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // A fresh canvas per mount: a canvas whose context was force-lost cannot be reused (StrictMode remounts)
    const canvas = document.createElement("canvas");
    canvas.setAttribute("aria-hidden", "true");
    Object.assign(canvas.style, { position: "absolute", inset: "0", width: "100%", height: "100%", display: "block", opacity: "0" });
    container.appendChild(canvas);

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let cancelled = false;
    let started = false;
    let inView = false;
    let shown = false;
    let raf = 0;
    let last = 0;
    let elapsed = 0;
    let renderer: T.WebGLRenderer | null = null;
    let handle: SceneHandle | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const draw = () => {
      if (!renderer || !handle) return;
      renderer.render(handle.scene, handle.camera);
      if (!shown) {
        shown = true;
        canvas.style.opacity = "1";
        setReady(true);
      }
    };
    const running = () => !!handle && inView && !document.hidden && !reducedMotion;
    const frame = (now: number) => {
      raf = 0;
      if (!running()) return;
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      elapsed += dt;
      handle!.update?.(dt, elapsed);
      draw();
      raf = requestAnimationFrame(frame);
    };
    const sync = () => {
      if (running()) {
        if (!raf) raf = requestAnimationFrame(frame);
      } else if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
      }
    };

    const start = async () => {
      started = true;
      const THREE = await import("three");
      if (cancelled) return;
      try {
        renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: "high-performance" });
      } catch {
        return; // no WebGL: the static fallback stays
      }
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min((window.devicePixelRatio || 1) * dprScale, maxDpr));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1;
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      const result = await setup({ THREE, renderer, canvas, container, reducedMotion, isCancelled: () => cancelled });
      if (cancelled || !result) {
        if (result) {
          result.dispose?.();
          disposeObject(result.scene);
        }
        return;
      }
      handle = result;

      const measure = () => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (!w || !h || !renderer || !handle) return;
        renderer.setSize(w, h, false);
        handle.resize(w, h);
        if (!running()) draw();
      };
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(container);
      measure();
      handle.update?.(0, 0);
      draw();
      sync();
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (inView && !started) void start();
        sync();
      },
      { rootMargin: "200px" },
    );
    io.observe(container);
    document.addEventListener("visibilitychange", sync);

    return () => {
      cancelled = true;
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
      resizeObserver?.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (handle) {
        handle.dispose?.();
        disposeObject(handle.scene);
      }
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
      }
      canvas.remove();
      handle = null;
      renderer = null;
      setReady(false);
    };
  }, [setup, dprScale, maxDpr]);

  return { containerRef, ready };
}
